import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { TimeZoneContext } from '@contexts/timezone-context';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { TextArea, TextBox } from '@progress/kendo-react-inputs';
import { convertLocalDateToUTC } from '@utils/dateUtils';
import I18n, { translate } from '@utils/i18n';
import api from '@utils/axios';
import { KendoIntlProvider } from '@contexts/kendo-intl-context';
import { newApiInternalTimeEntryPath, apiInternalTimeEntryPath, apiInternalTimeEntriesPath } from 'routes';

const namespace = 'features.time_entries.modal';
const dateTimeFormat = 'MM/dd/yyyy h:mm a';

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function TimeEntryModal({
  timeEntryId,
  trackedObject,
  onDataChanged,
  onClose,
  usersForSelect,
  ratesForSelect,
  visitTypesForSelect,
  timeZonesForSelect
}) {
  const [visible, setVisible] = useState(true);
  const [timeEntry, setTimeEntry] = useState({});
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [workOrdersLoading, setWorkOrdersLoading] = useState(false);
  const { timeZonesMapping } = useContext(TimeZoneContext);
  const debounceTimeout = useRef(null);

  const prevIsLoading = usePrevious(isLoading);
  const prevTrackedObjectType = usePrevious(timeEntry.tracked_object_type);

  const fetchWorkOrdersRef = useRef(null);

  useEffect(() => {
    setIsLoading(true);
    const abortController = new AbortController();

    if (timeEntryId) {
      api
        .get(apiInternalTimeEntryPath(timeEntryId), { signal: abortController.signal })
        .then(({ data }) => {
          const entry = data?.time_entry || {};
          const isOtherType = entry.tracked_object_type === null;
          if (isOtherType) {
            entry.tracked_object_type = 'Other';
          }
          entry.isOtherTypeRecord = isOtherType;
          if (entry.tracked_object_type === 'WorkOrder' && entry.tracked_object_id) {
            setWorkOrders([{ value: entry.tracked_object_id, label: entry.tracked_object_display_name }]);
          }
          setTimeEntry(entry);
        })
        .catch((error) => {
          if (error.name !== 'CanceledError') {
            console.error(error);
          }
        })
        .finally(() => setIsLoading(false));
    } else {
      const newTimeEntryData = {
        tracked_object_id: trackedObject?.id,
        tracked_object_type: trackedObject?.type || 'Other'
      };
      setTimeEntry(newTimeEntryData);

      api
        .get(newApiInternalTimeEntryPath(), {
          params: { tracked_object_type: newTimeEntryData.tracked_object_type },
          signal: abortController.signal
        })
        .then(({ data }) =>
          setTimeEntry((prev) => ({ ...prev, time_zone: data.time_zone, available_rates: data.rates || [] }))
        )
        .catch((error) => {
          if (error.name !== 'CanceledError') {
            console.error(error);
          }
        })
        .finally(() => setIsLoading(false));
    }

    return () => {
      abortController.abort();
    };
  }, [timeEntryId, trackedObject]);

  useEffect(() => {
    const typeChanged = prevTrackedObjectType !== undefined && prevTrackedObjectType !== timeEntry.tracked_object_type;
    if (!typeChanged) return;

    const newType = timeEntry.tracked_object_type;
    if (!newType) {
      setWorkOrders([]);
      return;
    }

    const abortController = new AbortController();

    api
      .get(newApiInternalTimeEntryPath(), {
        params: { tracked_object_type: newType },
        signal: abortController.signal
      })
      .then(({ data }) => {
        setTimeEntry((prev) => ({
          ...prev,
          available_rates: data.rates || [],
          rate_id: null,
          tracked_object_id: null
        }));
        if (newType === 'WorkOrder') {
          fetchWorkOrdersRef.current();
        } else {
          setWorkOrders([]);
        }
      })
      .catch((error) => {
        if (error.name !== 'CanceledError') {
          console.error(error);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [timeEntry.tracked_object_type, prevTrackedObjectType]);

  useEffect(() => {
    if (prevIsLoading && !isLoading) {
      if (timeEntry.tracked_object_type === 'WorkOrder') {
        fetchWorkOrdersRef.current();
      }
    }
  }, [isLoading, prevIsLoading, timeEntry.tracked_object_type]);

  const toggleDialog = () => {
    setVisible(!visible);
    onClose();
  };

  const handleChange = (event) => {
    setTimeEntry((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleDropDownChange = (name) => (event) => {
    setTimeEntry((prev) => ({ ...prev, [name]: event.value?.value }));
  };

  const fetchWorkOrders = useCallback(
    (filter = '') => {
      setWorkOrdersLoading(true);
      const params = {
        type: 'work_order',
        filter: {
          logic: 'or',
          filters: [{ value: filter, operator: 'contains' }]
        }
      };

      api
        .get('/lookup', { params })
        .then(({ data }) => {
          setWorkOrders((prevWorkOrders) => {
            const newWorkOrders = data.data || [];
            const selected = prevWorkOrders.find((wo) => wo.value === timeEntry.tracked_object_id);
            if (selected && !newWorkOrders.some((wo) => wo.value === selected.value)) {
              return [selected, ...newWorkOrders];
            }
            return newWorkOrders;
          });
        })
        .catch((error) => {
          if (error.name !== 'CanceledError') console.error(error);
        })
        .finally(() => {
          setWorkOrdersLoading(false);
        });
    },
    [timeEntry.tracked_object_id]
  );

  useEffect(() => {
    fetchWorkOrdersRef.current = fetchWorkOrders;
  });

  const handleWorkOrderFilterChange = useCallback(
    (event) => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        fetchWorkOrders(event.filter.value);
      }, 500);
    },
    [fetchWorkOrders]
  );

  const onSubmit = () => {
    const url = timeEntryId ? apiInternalTimeEntryPath(timeEntryId) : apiInternalTimeEntriesPath();
    const method = timeEntryId ? 'put' : 'post';

    const payload = {
      ...timeEntry,
      start_at:
        timeEntry.start_at &&
        convertLocalDateToUTC(new Date(timeEntry.start_at), timeZonesMapping[timeEntry.time_zone]),
      end_at:
        timeEntry.end_at && convertLocalDateToUTC(new Date(timeEntry.end_at), timeZonesMapping[timeEntry.time_zone]),
      time_zone_override: timeEntry.time_zone
    };

    if (payload.tracked_object_type === 'Other') {
      payload.tracked_object_type = null;
    }

    api[method](url, { time_entry: payload })
      .then(() => {
        const message = timeEntryId ? I18n.t('time_entries.update.success') : I18n.t('time_entries.create.success');
        onDataChanged(message);
        toggleDialog();
      })
      .catch(({ response }) => {
        if (response?.data) {
          setErrors(response.data.errors || response.data);
        }
      });
  };

  const rateOptions = timeEntry.available_rates || ratesForSelect;

  return (
    <KendoIntlProvider>
      {visible && (
        <Dialog
          title={translate(timeEntryId ? 'edit_title' : 'new_title', { namespace })}
          className="qmb-dialog"
          onClose={toggleDialog}
          width={600}>
          {!timeEntryId && <p>{translate('new_form_info', { namespace })}</p>}
          <table className="qmb-table--spreadsheet">
            <thead>
              <tr>
                <th style={{ width: '30%' }}>{translate('field', { namespace })}</th>
                <th>{translate('value', { namespace })}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="nested_fields">
                <td className="table__cell">
                  <div className="qmb-input--text">
                    <input value={translate('start_time', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-input--date--blank">
                    <DateTimePicker
                      name="start_at"
                      placeholder={translate('start_date_placeholder', { namespace })}
                      value={timeEntry.start_at ? new Date(timeEntry.start_at) : null}
                      onChange={handleChange}
                      format={dateTimeFormat}
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell">
                  <div className="qmb-input--text">
                    <input value={translate('end_time', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-input--date--blank">
                    <DateTimePicker
                      name="end_at"
                      placeholder={translate('end_date_placeholder', { namespace })}
                      value={timeEntry.end_at ? new Date(timeEntry.end_at) : null}
                      onChange={handleChange}
                      format={dateTimeFormat}
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell">
                  <div className="qmb-input--text">
                    <input value={translate('time_zone_label', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <DropDownList
                      name="time_zone"
                      data={timeZonesForSelect}
                      textField="label"
                      dataItemKey="value"
                      value={timeZonesForSelect.find((timeZone) => timeZone.value === timeEntry.time_zone)}
                      onChange={handleDropDownChange('time_zone')}
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell">
                  <div className="qmb-input--text">
                    <input value={translate('tracked_object_type_label', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <DropDownList
                      name="tracked_object_type"
                      data={visitTypesForSelect}
                      textField="label"
                      dataItemKey="value"
                      value={visitTypesForSelect.find((type) => type.value === timeEntry.tracked_object_type)}
                      onChange={handleDropDownChange('tracked_object_type')}
                      disabled={!timeEntryId || timeEntry.isOtherTypeRecord}
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell">
                  <div className="qmb-input--text">
                    <input value={translate('rate', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <DropDownList
                      name="rate_id"
                      defaultItem={{ label: translate('rate_id_placeholder', { namespace }), value: null }}
                      data={rateOptions}
                      textField="label"
                      dataItemKey="value"
                      value={rateOptions.find((r) => r.value === timeEntry.rate_id)}
                      onChange={handleDropDownChange('rate_id')}
                    />
                  </div>
                </td>
              </tr>
              {!isLoading && timeEntry.tracked_object_type && timeEntry.tracked_object_type !== 'Other' && (
                <tr className="nested_fields">
                  <td className="table__cell">
                    <div className="qmb-input--text">
                      <input value={timeEntry.tracked_object_type} disabled />
                    </div>
                  </td>
                  <td className="table__cell">
                    {timeEntry.tracked_object_type === 'Inspection' ? (
                      <div className="qmb-input--text">
                        <TextBox
                          name="tracked_object_id"
                          placeholder={translate('inspection_placeholder', { namespace })}
                          value={timeEntry.tracked_object_id || null}
                          onChange={handleChange}
                        />
                      </div>
                    ) : (
                      <div className="qmb-select--blank">
                        <DropDownList
                          name="tracked_object_id"
                          defaultItem={{ label: translate('work_order_placeholder', { namespace }), value: null }}
                          data={workOrders}
                          textField="label"
                          dataItemKey="value"
                          value={workOrders.find((wo) => wo.value === timeEntry.tracked_object_id)}
                          onChange={handleDropDownChange('tracked_object_id')}
                          filterable
                          onFilterChange={handleWorkOrderFilterChange}
                          loading={workOrdersLoading}
                          aria-label={timeEntry.tracked_object_type}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              )}
              <tr className="nested_fields">
                <td className="table__cell">
                  <div className="qmb-input--text">
                    <input value={translate('technician', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <DropDownList
                      name="technician_id"
                      defaultItem={{ label: translate('technician_id_placeholder', { namespace }), value: null }}
                      data={usersForSelect}
                      textField="label"
                      dataItemKey="value"
                      value={usersForSelect.find((t) => t.value === timeEntry.technician_id)}
                      onChange={handleDropDownChange('technician_id')}
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell" style={{ padding: 0, position: 'relative' }}>
                  <div
                    className="qmb-input--text"
                    style={{ position: 'absolute', top: -1, left: 0, right: 0, bottom: 0, display: 'flex' }}>
                    <input value={translate('notes', { namespace })} disabled style={{ flex: 1 }} />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <TextArea
                      name="notes"
                      placeholder={translate('notes_placeholder', { namespace })}
                      value={timeEntry.notes || ''}
                      onChange={handleChange}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          {errors.length > 0 && (
            <div style={{ marginTop: '16px', maxWidth: '100%', overflow: 'hidden' }}>
              {errors.map((error, i) => (
                <div
                  key={i}
                  style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px', maxWidth: '100%' }}>
                  <i
                    className="fa-solid fa-do-not-enter"
                    style={{ color: '#DB2B39', fontSize: '14px', marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
                  />
                  <span style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    {error}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="modal__footer pull-right" style={{ marginTop: '16px' }}>
            <button type="button" className="qmb-button" onClick={toggleDialog}>
              {I18n.t('generic.cancel')}
            </button>
            <button type="button" onClick={onSubmit} className="qmb-button--submit">
              {timeEntryId ? I18n.t('generic.update') : translate('new_title', { namespace })}
            </button>
          </div>
        </Dialog>
      )}
    </KendoIntlProvider>
  );
}

TimeEntryModal.propTypes = {
  timeEntryId: PropTypes.number,
  trackedObject: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string
  }),
  usersForSelect: PropTypes.arrayOf(PropTypes.object).isRequired,
  ratesForSelect: PropTypes.arrayOf(PropTypes.object).isRequired,
  visitTypesForSelect: PropTypes.arrayOf(PropTypes.object).isRequired,
  timeZonesForSelect: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDataChanged: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

TimeEntryModal.defaultProps = {
  timeEntryId: null,
  trackedObject: null
};

export default TimeEntryModal;
